import type { DailyChallengeClaim } from './dailyChallengeContracts';
import { parseDailyChallenges } from './dailyChallengeContracts';
import { parseReferralLog, type ReferralLogPage } from './referralLogContracts';
import { parseDominoResponse } from '../domino/DominoClient';
import type { DominoStackAction, DominoStackResult } from '../domino/DominoStack';
import type { DominoResponse, DominoAction } from '../domino/Domino';
import { validDominoOptions, type DominoOptions } from '../domino/Domino';
import { parseReferrals, type ReferralPage } from './referralContracts';
import { captureBrowserDisplayMetrics } from './browserDisplayMetrics';
import { parseCoinTransactions, type CoinTransactionPage } from './coinTransactionContracts';
import type { TabletopPlacement, TabletopAction } from '../world/TabletopScene';
import {
  parseWorldHistory,
  parseWorldHistorySubmission,
  type WorldHistoryRequest,
  type WorldHistoryInspection,
  type WorldHistorySubmission,
} from './worldHistoryContracts';
import { parseWorldNotificationUpdate } from './contracts';
import {
  parseCustomVehiclePickupSubmissionResponse,
  type CustomVehiclePickupSubmissionPayload,
} from './contracts';
import { parseGateOfferingResources, parseGateOfferingRevision } from './contracts';
import { generateBrowserUuid } from '../app/BrowserIdentity';
import {
  ContractError,
  parseErrorResponse,
  parseEntityChunkResponse,
  parseBallsResponse,
  parseRaceBuildingManifestResponse,
  parseRaceHeatResponse,
  parseRaceQueueResponse,
  parseRaceStartResponse,
  parseCurrentRaceResponse,
  parseEntityRevisionResponse,
  parseDecorationOccupancyResponse,
  parseObjectCollisionResponse,
  parsePresenceResponse,
  parseCharactersResponse,
  parseVillageChatResponse,
  parseChatSubmissionResponse,
  parseVIPWebsiteResponse,
  parseVIPWebsiteAgentResponse,
  parseLiveMessagesResponse,
  parseLiveMessageSubmissionResponse,
  parseForumIndexResponse,
  parseForumThreadsResponse,
  parseForumMessagesResponse,
  parseForumSearchResponse,
  parseForumThreadCreationResponse,
  parseForumReplyCreationResponse,
  parseForumMessageEditResponse,
  parseForumMessageDeletionResponse,
  parseForumThreadStateResponse,
  parseMovementSubmissionResponse,
  parseNotificationCursorResponse,
  parsePetTrickSubmissionResponse,
  parsePetTrainingSubmissionResponse,
  parsePetTreatSubmissionResponse,
  parseTreasurePickupSubmissionResponse,
  parseSeasonalBadgePickupSubmissionResponse,
  parseBuildingPurchaseSubmissionResponse,
  parseNoboPurchaseSubmissionResponse,
  parseNoboEditSubmissionResponse,
  parseMountSaleSubmissionResponse,
  parseBuildingCodeSubmissionResponse,
  parseBuildingTextSubmissionResponse,
  parseBuildingEditSubmissionResponse,
  parseVillageEditorResponse,
  parseBuildingBatchEditSubmissionResponse,
  parseBuildingBatchPickupSubmissionResponse,
  parseBuildingCatalogResponse,
  parseVillageExpansionSubmissionResponse,
  parseBuildingProductionResponse,
  parseBuildingProductionDeltaResponse,
  parseMarketplaceResponse,
  parseBuildingProductionSubmissionResponse,
  parseInventoryActivationSubmissionResponse,
  parseWeatherActivationSubmissionResponse,
  parseWeatherInventoryResponse,
  parseInventoryMoveSubmissionResponse,
  parseMountPurchaseSubmissionResponse,
  parseMountShopResponse,
  parsePetPurchaseSubmissionResponse,
  parsePetShopResponse,
  parseThemePurchaseSubmissionResponse,
  parseThemeShopResponse,
  parseMountTransitionSubmissionResponse,
  parseMountNameSubmissionResponse,
  parseBuildingTeleportSubmissionResponse,
  parseResourceSlotMoveSubmissionResponse,
  parseTombolaResponse,
  parsePictureDeletionResponse,
  parsePictureUploadResponse,
  parsePlayerPicturesResponse,
  parsePlayerProfileResponse,
  parsePlayerVillagesResponse,
  parsePlayerBadgesResponse,
  parseBlockedPlayerListResponse,
  parseOnlinePlayerListResponse,
  parseHelpDirectoryResponse,
  parseWardrobeResponse,
  parseVillageVisitorListResponse,
  parseFriendListResponse,
  parseFriendRemovalResponse,
  parseFriendRelationshipResponse,
  parseFriendRequestMutationResponse,
  parseGuestbookResponse,
  parseGuestbookAvailabilityResponse,
  parseGuestbookHistoryResponse,
  parseGuestbookMutationResponse,
  parseGuestbookSubmissionResponse,
  parseVillageTravelResponse,
  parseOwnedVillagesResponse,
  parseVisitorKickSubmissionResponse,
  parseDoorbellSubmissionResponse,
  parseSitSubmissionResponse,
  parseDiceRollResponse,
  type DiceRollPayload,
  parseCannonLaunchSubmissionResponse,
  parseSwingIntensitySubmissionResponse,
  parseAwaySubmissionResponse,
  parseAvatarGestureSubmissionResponse,
  parseSillyGooseStareSubmissionResponse,
  parseAvatarSpawnTeleportSubmissionResponse,
  parseAvatarEmoteSubmissionResponse,
  parseKeyDoorSubmissionResponse,
  parseSelfUnlockDoorSubmissionResponse,
  parsePetFollowingSubmissionResponse,
  parsePetHouseSubmissionResponse,
  parseSessionResumeResponse,
  parseNordTimeResponse,
  parsePasswordChangeResponse,
  parseLoginNewsResponse,
  parseSessionResponse,
  parseUserSettingsResponse,
  parseLegacyRedemptionResponse,
  parseTerrainResponse,
  parseTerrainWriteResponse,
  parseTerrainRevisionResponse,
  type AccountRegistration,
  type BrowserSession,
  type SessionResumePayload,
  type NordTimePayload,
  type PasswordChangePayload,
  type LoginNewsPayload,
  type TerrainPayload,
  type TerrainWritePayload,
  type EntityChunkPayload,
  type BallUpdatePayload,
  type RaceBuildingManifestPayload,
  type RaceHeatPayload,
  type RaceQueuePayload,
  type RaceStartPayload,
  type CurrentRacePayload,
  type EntityRevisionPayload,
  type DecorationOccupancyPayload,
  type ObjectCollisionPayload,
  type TerrainRevisionPayload,
  type VillagePresencePayload,
  type VisitorKickSubmissionPayload,
  type DoorbellSubmissionPayload,
  type SitSubmissionPayload,
  type CannonLaunchSubmissionPayload,
  type SwingIntensitySubmissionPayload,
  type AwaySubmissionPayload,
  type AvatarGestureAction,
  type AvatarGestureSubmissionPayload,
  type SillyGooseStareSubmissionPayload,
  type AvatarSpawnTeleportAction,
  type AvatarSpawnTeleportSubmissionPayload,
  type AvatarEmoteAction,
  type AvatarEmoteSubmissionPayload,
  type KeyDoorSubmissionPayload,
  type SelfUnlockDoorSubmissionPayload,
  type MovementSubmissionPayload,
  type FootballAdmissionPayload,
  type MoonwalkRaceTelemetryPayload,
  type NotificationCursorPayload,
  type PetTrickSubmissionPayload,
  type PetTrainingSubmissionPayload,
  type PetTreatSubmissionPayload,
  type TreasurePickupSubmissionPayload,
  type SeasonalBadgePickupSubmissionPayload,
  type BuildingPurchaseSubmissionPayload,
  type NoboPurchaseSubmissionPayload,
  type NoboEditOperation,
  type NoboEditSubmissionPayload,
  type MountSaleSubmissionPayload,
  type BuildingCodeSubmissionPayload,
  type BuildingTextSubmissionPayload,
  type BuildingGiftPurchaseDetails,
  type BuildingEditOperation,
  type BuildingEditSubmissionPayload,
  type VillageEditorPayload,
  type BuildingBatchPlacementPayload,
  type BuildingBatchEditSubmissionPayload,
  type BuildingBatchPickupSubmissionPayload,
  type BuildingCatalogPayload,
  type VillageExpansionSubmissionPayload,
  type BuildingProductionAction,
  type BuildingProductionPayload,
  type BuildingProductionDeltaPayload,
  type MarketplaceAuctionOptions,
  type MarketplacePayload,
  type BuildingProductionSubmissionPayload,
  type InventoryActivationSubmissionPayload,
  type WeatherActivationSubmissionPayload,
  type WeatherControlsPayload,
  type WeatherKind,
  type InventoryMoveSubmissionPayload,
  type MountPurchaseSubmissionPayload,
  type MountShopId,
  type MountShopPayload,
  type PetPurchaseSubmissionPayload,
  type PetShopPayload,
  type ThemePurchaseSubmissionPayload,
  type ThemeShopPayload,
  type MountTransitionAction,
  type MountTransitionReconciliationPayload,
  type MountTransitionSubmissionPayload,
  type MountNameSubmissionPayload,
  type BuildingTeleportSubmissionPayload,
  type ResourceSlotArea,
  type ResourceSlotMoveSubmissionPayload,
  type TombolaAction,
  type TombolaPayload,
  type PetFollowingSubmissionPayload,
  type PetHouseSubmissionPayload,
  type VillageCharactersPayload,
  type VillageChatPayload,
  type ChatSubmissionPayload,
  type VIPWebsitePayload,
  type VIPWebsiteAgentResult,
  type LiveMessagesPayload,
  type LiveMessageSubmissionPayload,
  type ForumIndexPayload,
  type ForumThreadPagePayload,
  type ForumMessagePagePayload,
  type ForumSearchPagePayload,
  type ForumThreadCreationPayload,
  type ForumReplyCreationPayload,
  type ForumMessageEditPayload,
  type ForumMessageDeletionPayload,
  type ForumThreadStatePayload,
  type PlayerPicturesPayload,
  type PlayerPersonalInformationUpdate,
  type PlayerProfilePayload,
  parseSupportIdentityResponse,
  type SupportIdentityPayload,
  type PlayerVillagesPayload,
  type PlayerBadgesPayload,
  type ContextorialCompletionPayload,
  type BlockedPlayerListPayload,
  type OnlinePlayerListPayload,
  type HelpDirectoryPayload,
  type WardrobePayload,
  type WardrobePurchaseSelection,
  type VillageVisitorListPayload,
  type FriendListPayload,
  type FriendRemovalPayload,
  type FriendRelationshipPayload,
  type FriendRequestMutationPayload,
  type GuestbookPayload,
  type GuestbookAvailabilityPayload,
  type GuestbookHistoryPayload,
  type GuestbookMutationPayload,
  type GuestbookSubmissionPayload,
  type VillageTravelPayload,
  type OwnedVillagesResponsePayload,
  type PictureDeletionPayload,
  type PictureUploadPayload,
  type ScreenshotUploadPayload,
  type LegacyLanguage,
  type UserSettingsPayload,
  type LegacyRedemptionPayload,
  parseScreenshotUploadResponse,
  parseContextorialCompletionResponse,
  parsePaymentCatalogResponse,
  parsePaymentCheckoutSessionResponse,
  parsePaymentCustomerPortalSessionResponse,
  parsePromoRedemptionResponse,
  type PaymentCatalogPayload,
  type PaymentCheckoutSessionPayload,
  type PaymentCustomerPortalSessionPayload,
  type PromoRedemptionPayload,
} from './contracts';
import {
  parseModerationActionResponse,
  parseModerationBansResponse,
  parseModerationCommunicationsResponse,
  parseModerationDashboardResponse,
  parseModerationServerStatusResponse,
  parseModerationOnlinePlayersResponse,
  parseModerationPlayerListResponse,
  parseModerationPlayerResponse,
  type ModerationActionPayload,
  type ModerationBanPayload,
  type ModerationCommunicationPayload,
  type ModerationDashboardPayload,
  type ModerationServerStatusPayload,
  type ModerationOnlinePlayersPayload,
  type ModerationPlayerDetailPayload,
  type ModerationPlayerListPayload,
  type ModerationPlayerSort,
  type ModerationSortDirection,
} from './moderationContracts';
import type { LegacySystemSettings } from '../settings/LegacySystemSettings';
import {
  WorldRealtimeClient,
  WorldRealtimeResponseError,
  type WorldRealtimeHello,
  type WorldRealtimeTicket,
} from './WorldRealtimeClient';
import { FootballRealtimeClient, FootballRealtimeResponseError } from './FootballRealtimeClient';
export class GatewayClient {
  private worldRealtime: WorldRealtimeClient | undefined;
  private worldRealtimeConnection:
    Readonly<{ key: string; promise: Promise<WorldRealtimeClient> }> | undefined;
  private worldRealtimeGeneration = 0;
  private footballRealtime: FootballRealtimeClient | undefined;
  private footballRealtimeAdmission:
    Readonly<{ token: string; admission: FootballAdmissionPayload }> | undefined;

  constructor(private readonly language: LegacyLanguage = 'english') {}

  async readLoginNews(signal?: AbortSignal): Promise<LoginNewsPayload> {
    const headers = new Headers();
    headers.set('X-Nord-Language', this.language);
    const response = await this.request(
      '/api/v2/login-news',
      signal === undefined ? { headers } : { signal, headers },
    );
    return parseLoginNewsResponse(response);
  }

  async createAccount(registration: AccountRegistration, clientIdentity: string): Promise<void> {
    await this.request('/api/v2/accounts', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Nord-Language': this.language },
      body: JSON.stringify({ ...registration, clientIdentity }),
    });
  }

  async createSession(
    username: string,
    password: string,
    clientIdentity: string,
  ): Promise<BrowserSession> {
    const response = await this.request('/api/v2/sessions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        username,
        password,
        clientIdentity,
        display: captureBrowserDisplayMetrics(),
      }),
    });
    return parseSessionResponse(response);
  }

  async readSession(token: string, signal?: AbortSignal): Promise<BrowserSession> {
    const response = await this.request(
      '/api/v2/sessions/current',
      this.authorized(token, signal === undefined ? {} : { signal }),
    );
    return parseSessionResponse(response, token);
  }

  async readModerationReferrals(
    token: string,
    query: string,
    status: string,
    beforeId: number,
    signal: AbortSignal,
  ): Promise<ReferralLogPage> {
    const params = new URLSearchParams({ q: query, status, beforeId: String(beforeId) });
    return parseReferralLog(
      await this.request(
        `/api/v2/sessions/current/moderation/referrals?${params}`,
        this.authorized(token, { signal }),
      ),
    );
  }

  async readReferrals(token: string, beforeId: number, signal: AbortSignal): Promise<ReferralPage> {
    const query = new URLSearchParams();
    if (beforeId > 0) query.set('beforeId', String(beforeId));
    return parseReferrals(
      await this.request(
        `/api/v2/sessions/current/referrals?${query.toString()}`,
        this.authorized(token, { signal }),
      ),
    );
  }

  async dailyChallenges(
    token: string,
    claim: DailyChallengeClaim | undefined,
    signal: AbortSignal,
  ) {
    return parseDailyChallenges(
      await this.request(
        '/api/v2/sessions/current/daily-challenges',
        this.authorized(
          token,
          claim === undefined
            ? { signal, cache: 'no-store' }
            : {
                method: 'POST',
                cache: 'no-store',
                headers: { 'Content-Type': 'application/json' },
                signal,
                body: JSON.stringify(typeof claim === 'string' ? { week: claim } : claim),
              },
        ),
      ),
    );
  }

  async readCoinTransactions(
    token: string,
    source: string,
    beforeId: number,
    signal: AbortSignal,
  ): Promise<CoinTransactionPage> {
    const query = new URLSearchParams();
    if (source !== '') query.set('source', source);
    if (beforeId > 0) query.set('beforeId', String(beforeId));
    return parseCoinTransactions(
      await this.request(
        `/api/v2/sessions/current/coin-transactions?${query.toString()}`,
        this.authorized(token, { signal }),
      ),
    );
  }

  async readLegacyRedemption(
    token: string,
    signal?: AbortSignal,
  ): Promise<LegacyRedemptionPayload> {
    const response = await this.request(
      '/api/v2/sessions/current/legacy-redemption',
      this.authorized(token, signal === undefined ? {} : { signal }),
    );
    return parseLegacyRedemptionResponse(response);
  }

  async redeemLegacyAccount(
    token: string,
    username: string,
    password: string,
    signal?: AbortSignal,
  ): Promise<LegacyRedemptionPayload> {
    const response = await this.request(
      '/api/v2/sessions/current/legacy-redemption',
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseLegacyRedemptionResponse(response);
  }

  async updateUserSettings(
    token: string,
    language: LegacyLanguage,
    system: LegacySystemSettings,
    signal?: AbortSignal,
  ): Promise<UserSettingsPayload> {
    const response = await this.request(
      '/api/v2/sessions/current/settings',
      this.authorized(token, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ language, system }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseUserSettingsResponse(response);
  }

  async resumeSession(token: string, signal?: AbortSignal): Promise<SessionResumePayload> {
    const response = await this.request(
      '/api/v2/sessions/current/resume',
      this.authorized(token, {
        method: 'POST',
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseSessionResumeResponse(response, token);
  }

  async readNordTime(token: string, signal?: AbortSignal): Promise<NordTimePayload> {
    const response = await this.request(
      '/api/v2/sessions/current/server-time',
      this.authorized(token, signal === undefined ? {} : { signal }),
    );
    return parseNordTimeResponse(response);
  }

  async deleteSession(token: string, signal?: AbortSignal, keepalive = false): Promise<void> {
    this.disconnectRealtime();
    await this.request(
      '/api/v2/sessions/current',
      this.authorized(token, {
        method: 'DELETE',
        keepalive,
        ...(signal === undefined ? {} : { signal }),
      }),
      true,
    );
  }

  disconnectRealtime(): void {
    this.worldRealtimeGeneration++;
    this.worldRealtime?.close();
    this.worldRealtime = undefined;
    this.worldRealtimeConnection = undefined;
  }

  async changePassword(
    token: string,
    sequence: number,
    currentPassword: string,
    newPassword: string,
    repeatedPassword: string,
    signal?: AbortSignal,
  ): Promise<PasswordChangePayload> {
    const response = await this.request(
      '/api/v2/sessions/current/password',
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sequence,
          currentPassword,
          newPassword,
          repeatedPassword,
        }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parsePasswordChangeResponse(response);
  }

  async readTerrain(
    token: string,
    villageId: number,
    signal?: AbortSignal,
  ): Promise<TerrainPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/terrain`,
      this.authorized(token, signal === undefined ? {} : { signal }),
    );
    return parseTerrainResponse(response);
  }

  async writeTerrain(
    token: string,
    villageId: number,
    baseRevision: number,
    heightSamples: string,
    signal?: AbortSignal,
  ): Promise<TerrainWritePayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/terrain`,
      this.authorized(token, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          baseRevision,
          heightEncoding: 'signed-int8-base64',
          heightSamples,
        }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseTerrainWriteResponse(response);
  }

  async readEntityChunk(
    token: string,
    villageId: number,
    chunkX: number,
    chunkZ: number,
    signal?: AbortSignal,
  ): Promise<EntityChunkPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/entity-chunks/${String(chunkX)}/${String(chunkZ)}`,
      this.authorized(token, signal === undefined ? {} : { signal }),
    );
    return parseEntityChunkResponse(response);
  }

  async readBalls(
    token: string,
    villageId: number,
    signal?: AbortSignal,
  ): Promise<BallUpdatePayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/balls`,
      this.authorized(token, signal === undefined ? {} : { signal }),
    );
    return parseBallsResponse(response);
  }

  async readRaceBuildings(
    token: string,
    villageId: number,
    signal?: AbortSignal,
  ): Promise<RaceBuildingManifestPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/race-buildings`,
      this.authorized(token, signal === undefined ? {} : { signal }),
    );
    return parseRaceBuildingManifestResponse(response);
  }

  async startRaceHeat(
    token: string,
    villageId: number,
    triggerBuildingId: number,
    signal?: AbortSignal,
  ): Promise<RaceStartPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/race-heats`,
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ triggerBuildingId }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseRaceStartResponse(response);
  }

  async readCurrentRaceHeat(
    token: string,
    villageId: number,
    signal?: AbortSignal,
  ): Promise<CurrentRacePayload | null> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/race-heats/current`,
      this.authorized(token, signal === undefined ? {} : { signal }),
    );
    return parseCurrentRaceResponse(response);
  }

  async cancelRaceHeat(
    token: string,
    villageId: number,
    heatId: string,
    signal?: AbortSignal,
  ): Promise<RaceHeatPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/race-heats/${encodeURIComponent(heatId)}/participants/current`,
      this.authorized(token, { method: 'DELETE', ...(signal === undefined ? {} : { signal }) }),
    );
    const heat = parseRaceHeatResponse(response);
    if (heat === null) throw new ContractError('The cancelled race heat is missing');
    return heat;
  }

  async cancelRaceQueueParticipant(
    token: string,
    queueId: string,
    signal?: AbortSignal,
  ): Promise<RaceQueuePayload | null> {
    const response = await this.request(
      `/api/v2/sessions/current/race-queue/${encodeURIComponent(queueId)}/participants/current`,
      this.authorized(token, { method: 'DELETE', ...(signal === undefined ? {} : { signal }) }),
    );
    return parseRaceQueueResponse(response);
  }

  async readTerrainRevision(
    token: string,
    villageId: number,
    signal?: AbortSignal,
    afterRevision?: number,
  ): Promise<TerrainRevisionPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/terrain-revision${afterRevision === undefined ? '' : `?afterRevision=${String(afterRevision)}`}`,
      this.authorized(token, signal === undefined ? {} : { signal }),
    );
    return parseTerrainRevisionResponse(response);
  }

  async readEntityRevision(
    token: string,
    villageId: number,
    signal?: AbortSignal,
  ): Promise<EntityRevisionPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/entity-revision`,
      this.authorized(token, signal === undefined ? {} : { signal }),
    );
    return parseEntityRevisionResponse(response);
  }

  async readDecorationOccupancy(
    token: string,
    villageId: number,
    signal?: AbortSignal,
  ): Promise<DecorationOccupancyPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/decoration-occupancy`,
      this.authorized(token, signal === undefined ? {} : { signal }),
    );
    return parseDecorationOccupancyResponse(response);
  }

  async readObjectCollision(
    token: string,
    villageId: number,
    signal?: AbortSignal,
  ): Promise<ObjectCollisionPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/object-collision`,
      this.authorized(token, signal === undefined ? {} : { signal }),
    );
    return parseObjectCollisionResponse(response);
  }

  async readBuildingCatalog(
    token: string,
    villageId: number,
    signal?: AbortSignal,
  ): Promise<BuildingCatalogPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/building-catalog`,
      this.localizedAuthorized(token, signal === undefined ? {} : { signal }),
    );
    return parseBuildingCatalogResponse(response);
  }

  async expandVillage(
    token: string,
    villageId: number,
    expectedSize: number,
    signal?: AbortSignal,
  ): Promise<VillageExpansionSubmissionPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/expansions`,
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ expectedSize }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseVillageExpansionSubmissionResponse(response);
  }

  async readBuildingProduction(
    token: string,
    villageId: number,
    signal?: AbortSignal,
  ): Promise<BuildingProductionPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/building-production`,
      this.localizedAuthorized(token, signal === undefined ? {} : { signal }),
    );
    return parseBuildingProductionResponse(response);
  }

  async readBuildingProductionDelta(
    token: string,
    villageId: number,
    buildingIds: readonly number[],
    signal?: AbortSignal,
  ): Promise<BuildingProductionDeltaPayload> {
    if (
      buildingIds.length < 1 ||
      buildingIds.length > 200 ||
      new Set(buildingIds).size !== buildingIds.length ||
      buildingIds.some((id) => !Number.isInteger(id) || id === 0 || id < -32768 || id > 32767)
    )
      throw new Error('Provide 1–200 unique, valid building IDs.');
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/building-production?buildingIds=${encodeURIComponent(buildingIds.join(','))}`,
      this.localizedAuthorized(token, signal === undefined ? {} : { signal }),
    );
    return parseBuildingProductionDeltaResponse(response);
  }

  async readPresence(
    token: string,
    villageId: number,
    signal?: AbortSignal,
  ): Promise<VillagePresencePayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/presence`,
      this.authorized(token, signal === undefined ? {} : { signal }),
    );
    return parsePresenceResponse(response);
  }

  async yeetPlayer(
    token: string,
    villageId: number,
    playerId: number,
    warning: boolean,
    signal: AbortSignal,
  ): Promise<void> {
    await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/yeets`,
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ playerId, warning }),
        signal,
      }),
    );
  }

  async kickVisitor(
    token: string,
    villageId: number,
    playerId: number,
    signal?: AbortSignal,
  ): Promise<VisitorKickSubmissionPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/visitor-kicks`,
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ playerId }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseVisitorKickSubmissionResponse(response);
  }

  async ringDoorbell(
    token: string,
    villageId: number,
    buildingId: number,
    signal?: AbortSignal,
  ): Promise<DoorbellSubmissionPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/doorbells`,
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ buildingId }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseDoorbellSubmissionResponse(response);
  }

  async sitOnBuilding(
    token: string,
    villageId: number,
    buildingId: number,
    signal?: AbortSignal,
  ): Promise<SitSubmissionPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/sits`,
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ buildingId }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseSitSubmissionResponse(response);
  }

  async dominoes(
    token: string,
    villageId: number,
    since: string,
    action: DominoAction | undefined,
    signal: AbortSignal,
  ): Promise<DominoResponse> {
    return parseDominoResponse(
      await this.request(
        `/api/v2/sessions/current/villages/${String(villageId)}/dominoes${action === undefined ? `?since=${encodeURIComponent(since)}` : ''}`,
        this.authorized(token, {
          signal,
          ...(action === undefined
            ? {}
            : {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(action),
              }),
        }),
      ),
    );
  }

  async dominoStack(
    token: string,
    villageId: number,
    action: DominoStackAction,
    signal: AbortSignal,
  ): Promise<DominoStackResult> {
    const result = (await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/domino-stack-actions`,
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(action),
        signal,
      }),
    )) as DominoStackResult;
    if (
      !Number.isSafeInteger(result.entityRevision) ||
      !Number.isSafeInteger(result.generation) ||
      (result.options !== undefined && !validDominoOptions(result.options))
    )
      throw new Error('Invalid domino stack response');
    return result;
  }

  async rollDice(
    token: string,
    villageId: number,
    buildingId: number,
    signal?: AbortSignal,
    pieceId?: number,
  ): Promise<DiceRollPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/dice-rolls`,
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ buildingId, pieceId }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseDiceRollResponse(response);
  }

  async launchFromCannon(
    token: string,
    villageId: number,
    buildingId: number,
    signal?: AbortSignal,
  ): Promise<CannonLaunchSubmissionPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/cannon-launches`,
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ buildingId }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseCannonLaunchSubmissionResponse(response);
  }

  async setSwingIntensity(
    token: string,
    villageId: number,
    buildingId: number,
    intensity: number,
    angleMilli: number,
    angularVelocityMilli: number,
    signal?: AbortSignal,
  ): Promise<SwingIntensitySubmissionPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/swing-intensity`,
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ buildingId, intensity, angleMilli, angularVelocityMilli }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseSwingIntensitySubmissionResponse(response);
  }

  async setAway(
    token: string,
    villageId: number,
    signal?: AbortSignal,
  ): Promise<AwaySubmissionPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/away`,
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseAwaySubmissionResponse(response);
  }

  async performAvatarGesture(
    token: string,
    villageId: number,
    targetPlayerId: number,
    action: AvatarGestureAction,
    signal?: AbortSignal,
  ): Promise<AvatarGestureSubmissionPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/gestures`,
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetPlayerId, action }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseAvatarGestureSubmissionResponse(response);
  }

  async stareAtSillyGoose(
    token: string,
    villageId: number,
    goosePlayerId: number,
    signal?: AbortSignal,
  ): Promise<SillyGooseStareSubmissionPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/silly-goose-stares`,
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetPlayerId: goosePlayerId }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseSillyGooseStareSubmissionResponse(response);
  }

  async performAvatarSpawnTeleport(
    token: string,
    villageId: number,
    targetPlayerId: number,
    action: AvatarSpawnTeleportAction,
    signal?: AbortSignal,
  ): Promise<AvatarSpawnTeleportSubmissionPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/spawn-teleports`,
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetPlayerId, action }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseAvatarSpawnTeleportSubmissionResponse(response);
  }

  async performAvatarEmote(
    token: string,
    villageId: number,
    action: AvatarEmoteAction,
    signal?: AbortSignal,
  ): Promise<AvatarEmoteSubmissionPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/emotes`,
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseAvatarEmoteSubmissionResponse(response);
  }

  async activateKeyDoor(
    token: string,
    villageId: number,
    buildingId: number,
    code: string,
    signal?: AbortSignal,
  ): Promise<KeyDoorSubmissionPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/key-doors`,
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ buildingId, code }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseKeyDoorSubmissionResponse(response);
  }

  async activateSelfUnlockDoor(
    token: string,
    villageId: number,
    buildingId: number,
    code: string,
    signal?: AbortSignal,
  ): Promise<SelfUnlockDoorSubmissionPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/self-unlock-doors`,
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ buildingId, code }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseSelfUnlockDoorSubmissionResponse(response);
  }

  async readCharacters(
    token: string,
    villageId: number,
    signal?: AbortSignal,
  ): Promise<VillageCharactersPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/characters`,
      this.authorized(token, signal === undefined ? {} : { signal }),
    );
    return parseCharactersResponse(response);
  }

  async readVillageChat(
    token: string,
    villageId: number,
    signal?: AbortSignal,
  ): Promise<VillageChatPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/chat`,
      this.authorized(token, signal === undefined ? {} : { signal }),
    );
    return parseVillageChatResponse(response);
  }

  async readLiveMessages(token: string, signal?: AbortSignal): Promise<LiveMessagesPayload> {
    const response = await this.request(
      '/api/v2/sessions/current/live-messages',
      this.authorized(token, signal === undefined ? {} : { signal }),
    );
    return parseLiveMessagesResponse(response);
  }

  async removeVillageChatMessage(
    token: string,
    villageId: number,
    sequence: number,
    signal?: AbortSignal,
  ): Promise<VillageChatPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/chat/${String(sequence)}`,
      this.authorized(token, {
        method: 'DELETE',
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseVillageChatResponse(response);
  }

  async removeLiveMessage(
    token: string,
    sequence: number,
    signal?: AbortSignal,
  ): Promise<LiveMessagesPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/live-messages/${String(sequence)}`,
      this.authorized(token, {
        method: 'DELETE',
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseLiveMessagesResponse(response);
  }

  async readForumIndex(token: string, signal?: AbortSignal): Promise<ForumIndexPayload> {
    const response = await this.request(
      '/api/v2/sessions/current/forum',
      this.authorized(token, signal === undefined ? {} : { signal }),
    );
    return parseForumIndexResponse(response);
  }

  async readForumThreads(
    token: string,
    forumId: number,
    page: number,
    signal?: AbortSignal,
  ): Promise<ForumThreadPagePayload> {
    const response = await this.request(
      `/api/v2/sessions/current/forum/forums/${String(forumId)}/pages/${String(page)}`,
      this.authorized(token, signal === undefined ? {} : { signal }),
    );
    return parseForumThreadsResponse(response);
  }

  async readForumMessages(
    token: string,
    threadId: number,
    page: number,
    signal?: AbortSignal,
  ): Promise<ForumMessagePagePayload> {
    const response = await this.request(
      `/api/v2/sessions/current/forum/threads/${String(threadId)}/pages/${String(page)}`,
      this.authorized(token, signal === undefined ? {} : { signal }),
    );
    return parseForumMessagesResponse(response);
  }

  async searchForum(
    token: string,
    query: string,
    page: number,
    signal?: AbortSignal,
  ): Promise<ForumSearchPagePayload> {
    const response = await this.request(
      '/api/v2/sessions/current/forum/searches',
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query, page }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseForumSearchResponse(response);
  }

  async createForumThread(
    token: string,
    forumId: number,
    name: string,
    message: string,
    signal?: AbortSignal,
  ): Promise<ForumThreadCreationPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/forum/forums/${String(forumId)}/threads`,
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, message }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseForumThreadCreationResponse(response);
  }

  async addForumReply(
    token: string,
    threadId: number,
    message: string,
    signal?: AbortSignal,
  ): Promise<ForumReplyCreationPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/forum/threads/${String(threadId)}/messages`,
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseForumReplyCreationResponse(response);
  }

  async editForumMessage(
    token: string,
    threadId: number,
    messageId: number,
    message: string,
    signal?: AbortSignal,
  ): Promise<ForumMessageEditPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/forum/threads/${String(threadId)}/messages/${String(messageId)}`,
      this.authorized(token, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseForumMessageEditResponse(response);
  }

  async deleteForumMessage(
    token: string,
    threadId: number,
    messageId: number,
    signal?: AbortSignal,
  ): Promise<ForumMessageDeletionPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/forum/threads/${String(threadId)}/messages/${String(messageId)}`,
      this.authorized(token, {
        method: 'DELETE',
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseForumMessageDeletionResponse(response);
  }

  async setForumThreadSticky(
    token: string,
    threadId: number,
    sticky: boolean,
    signal?: AbortSignal,
  ): Promise<ForumThreadStatePayload> {
    return this.setForumThreadState(token, threadId, 'sticky', sticky, signal);
  }

  async setForumThreadLocked(
    token: string,
    threadId: number,
    locked: boolean,
    signal?: AbortSignal,
  ): Promise<ForumThreadStatePayload> {
    return this.setForumThreadState(token, threadId, 'locked', locked, signal);
  }

  private async setForumThreadState(
    token: string,
    threadId: number,
    field: 'sticky' | 'locked',
    value: boolean,
    signal?: AbortSignal,
  ): Promise<ForumThreadStatePayload> {
    const response = await this.request(
      `/api/v2/sessions/current/forum/threads/${String(threadId)}/${field}`,
      this.authorized(token, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: value }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseForumThreadStateResponse(response, field);
  }

  async waitForNotifications(
    token: string,
    villageId: number,
    streamGeneration: number,
    afterCursor: number,
    entityRevision: number,
    signal?: AbortSignal,
  ): Promise<NotificationCursorPayload> {
    if (typeof WebSocket === 'undefined' || !isCanonicalSessionToken(token)) {
      return this.waitForNotificationsHTTP(
        token,
        villageId,
        streamGeneration,
        afterCursor,
        entityRevision,
        signal,
      );
    }
    const realtime = await this.worldRealtimeFor(
      token,
      { villageId, streamGeneration, lastCursor: afterCursor, entityRevision },
      signal,
    );
    const response = await realtime.nextNotification(signal);
    return parseNotificationCursorResponse(response);
  }

  async submitVillageChat(
    token: string,
    villageId: number,
    text: string,
    signal?: AbortSignal,
  ): Promise<ChatSubmissionPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/chat`,
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseChatSubmissionResponse(response);
  }

  async readVIPWebsite(
    token: string,
    villageId: number,
    signal?: AbortSignal,
  ): Promise<VIPWebsitePayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/vip-website`,
      this.authorized(token, signal === undefined ? {} : { signal }),
    );
    return parseVIPWebsiteResponse(response);
  }

  async editVIPWebsite(
    token: string,
    villageId: number,
    prompt: string,
    signal?: AbortSignal,
  ): Promise<VIPWebsiteAgentResult> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/vip-website/agent`,
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseVIPWebsiteAgentResponse(response);
  }

  async submitLiveMessage(
    token: string,
    sequence: number,
    text: string,
    signal?: AbortSignal,
  ): Promise<LiveMessageSubmissionPayload> {
    const response = await this.request(
      '/api/v2/sessions/current/live-messages',
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sequence, text }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseLiveMessageSubmissionResponse(response);
  }

  async submitMovement(
    token: string,
    villageId: number,
    sequence: number,
    posX: number,
    posY: number,
    posZ: number,
    rotation: number,
    boostActive?: true,
    signal?: AbortSignal,
  ): Promise<MovementSubmissionPayload> {
    if (typeof WebSocket === 'undefined' || !isCanonicalSessionToken(token)) {
      return this.submitMovementHTTP(
        token,
        villageId,
        sequence,
        posX,
        posY,
        posZ,
        rotation,
        boostActive,
        signal,
      );
    }
    if (
      this.footballRealtime === undefined &&
      this.footballRealtimeAdmission?.token === token &&
      this.footballRealtimeAdmission.admission.villageId === villageId &&
      this.footballRealtimeAdmission.admission.expiresAt > Date.now()
    ) {
      this.activateFootballRealtime(token, this.footballRealtimeAdmission.admission);
    }
    const football = this.footballRealtime;
    if (football?.token === token && football.villageId === villageId) {
      if (football.available) {
        try {
          const response = await football.submitMovement(
            {
              sequence,
              posX,
              posY,
              posZ,
              rotation,
              ...(boostActive === true ? { boostActive: true } : {}),
            },
            signal,
          );
          const parsed = parseMovementSubmissionResponse(response);
          if (parsed.footballAdmission !== undefined)
            this.activateFootballRealtime(token, parsed.footballAdmission);
          return parsed;
        } catch (cause) {
          if (cause instanceof FootballRealtimeResponseError) {
            const response = cause.response as { code?: unknown };
            throw new GatewayError(
              typeof response.code === 'string' ? response.code : 'football_frame_rejected',
              'The football movement frame was rejected.',
              409,
              cause,
            );
          }
          football.close();
          if (this.footballRealtime === football) this.footballRealtime = undefined;
          this.footballRealtimeAdmission = undefined;
          // The football outcome is unknown after reconnect exhaustion. Submit the newest
          // sampled pose through the durable world socket before asking rendering to resync.
          const realtime = await this.worldRealtimeFor(
            token,
            { villageId, streamGeneration: 1, lastCursor: 0, entityRevision: 0 },
            signal,
          );
          try {
            const response = await realtime.submitMovement(
              {
                sequence,
                posX,
                posY,
                posZ,
                rotation,
                ...(boostActive === true ? { boostActive: true } : {}),
              },
              signal,
            );
            const parsed = parseMovementSubmissionResponse(response);
            if (parsed.footballAdmission !== undefined)
              this.activateFootballRealtime(token, parsed.footballAdmission);
            return parsed;
          } catch (fallbackCause) {
            if (!(fallbackCause instanceof WorldRealtimeResponseError)) throw cause;
            const error = parseErrorResponse(fallbackCause.response);
            throw new GatewayError(
              error.code,
              error.message,
              409,
              fallbackCause,
              undefined,
              undefined,
              undefined,
              undefined,
              undefined,
              undefined,
              undefined,
              error.movementRecovery,
              error.buildingBatch,
            );
          }
        }
      }
      football.close();
      this.footballRealtime = undefined;
      this.footballRealtimeAdmission = undefined;
    }
    const realtime = await this.worldRealtimeFor(
      token,
      { villageId, streamGeneration: 1, lastCursor: 0, entityRevision: 0 },
      signal,
    );
    try {
      const response = await realtime.submitMovement(
        {
          sequence,
          posX,
          posY,
          posZ,
          rotation,
          ...(boostActive === true ? { boostActive: true } : {}),
        },
        signal,
      );
      const parsed = parseMovementSubmissionResponse(response);
      if (parsed.footballAdmission !== undefined)
        this.activateFootballRealtime(token, parsed.footballAdmission);
      return parsed;
    } catch (cause) {
      if (!(cause instanceof WorldRealtimeResponseError)) throw cause;
      const error = parseErrorResponse(cause.response);
      throw new GatewayError(
        error.code,
        error.message,
        409,
        cause,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        error.movementRecovery,
      );
    }
  }

  private activateFootballRealtime(token: string, admission: FootballAdmissionPayload): void {
    this.footballRealtimeAdmission = { token, admission };
    const current = this.footballRealtime;
    if (
      current?.token === token &&
      current.villageId === admission.villageId &&
      current.available
    ) {
      current.updateAdmission(admission);
      return;
    }
    current?.close();
    this.footballRealtime = new FootballRealtimeClient(token, admission);
  }

  private async waitForNotificationsHTTP(
    token: string,
    villageId: number,
    streamGeneration: number,
    afterCursor: number,
    entityRevision: number,
    signal?: AbortSignal,
  ): Promise<NotificationCursorPayload> {
    const query = new URLSearchParams({
      villageId: String(villageId),
      streamGeneration: String(streamGeneration),
      after: String(afterCursor),
      entityRevision: String(entityRevision),
      waitMilliseconds: '10000',
    });
    const response = await this.request(
      `/api/v2/sessions/current/notifications?${query.toString()}`,
      this.authorized(token, signal === undefined ? {} : { signal }),
    );
    return parseNotificationCursorResponse(response);
  }

  private async submitMovementHTTP(
    token: string,
    villageId: number,
    sequence: number,
    posX: number,
    posY: number,
    posZ: number,
    rotation: number,
    boostActive?: true,
    signal?: AbortSignal,
  ): Promise<MovementSubmissionPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/presence/movements`,
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sequence,
          posX,
          posY,
          posZ,
          rotation,
          ...(boostActive === true ? { boostActive: true } : {}),
        }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseMovementSubmissionResponse(response);
  }

  private async worldRealtimeFor(
    token: string,
    hello: WorldRealtimeHello,
    signal?: AbortSignal,
  ): Promise<WorldRealtimeClient> {
    const key = `${token}:${String(hello.villageId)}`;
    const realtime = this.worldRealtime;
    if (
      realtime !== undefined &&
      !realtime.isClosed &&
      realtime.token === token &&
      realtime.hello.villageId === hello.villageId
    ) {
      await realtime.connect();
      return realtime;
    }
    const connecting = this.worldRealtimeConnection;
    if (connecting?.key === key) return connecting.promise;

    realtime?.close();
    this.worldRealtime = undefined;
    const generation = ++this.worldRealtimeGeneration;
    const promise = this.createWorldRealtime(token, hello, generation, signal);
    const connection = { key, promise };
    this.worldRealtimeConnection = connection;
    try {
      return await promise;
    } finally {
      if (this.worldRealtimeConnection === connection) this.worldRealtimeConnection = undefined;
    }
  }

  private async createWorldRealtime(
    token: string,
    hello: WorldRealtimeHello,
    generation: number,
    signal?: AbortSignal,
  ): Promise<WorldRealtimeClient> {
    const response = await this.request(
      '/api/v2/sessions/current/realtime-ticket',
      this.authorized(token, {
        method: 'POST',
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    if (generation !== this.worldRealtimeGeneration) {
      throw new Error('The realtime connection was superseded.');
    }
    const ticket = parseWorldRealtimeTicket(response);
    const realtime = new WorldRealtimeClient(token, hello, ticket);
    try {
      await realtime.connect();
      if (generation !== this.worldRealtimeGeneration) {
        realtime.close();
        throw new Error('The realtime connection was superseded.');
      }
      this.worldRealtime = realtime;
      return realtime;
    } catch (error) {
      if (this.worldRealtime === realtime) this.worldRealtime = undefined;
      realtime.close();
      throw error;
    }
  }

  async submitRaceFinish(
    token: string,
    villageId: number,
    elapsedMilliseconds: number,
    moonwalkTelemetry?: MoonwalkRaceTelemetryPayload,
    signal?: AbortSignal,
  ): Promise<void> {
    await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/race-finishes`,
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          elapsedMilliseconds,
          ...(moonwalkTelemetry === undefined ? {} : { moonwalkTelemetry }),
        }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
  }

  async submitAuthoritativeRaceFinish(
    token: string,
    villageId: number,
    heatId: string,
    finishBuildingId: number,
    finishNonce: string,
    moonwalkTelemetry?: MoonwalkRaceTelemetryPayload,
    signal?: AbortSignal,
  ): Promise<void> {
    await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/race-finishes`,
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          heatId,
          finishBuildingId,
          finishNonce,
          ...(moonwalkTelemetry === undefined ? {} : { moonwalkTelemetry }),
        }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
  }

  async submitPetTrick(
    token: string,
    villageId: number,
    trick: number,
    signal?: AbortSignal,
  ): Promise<PetTrickSubmissionPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/pet/tricks`,
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ trick }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parsePetTrickSubmissionResponse(response);
  }

  async submitPetTraining(
    token: string,
    villageId: number,
    targetPlayerId: number,
    signal?: AbortSignal,
  ): Promise<PetTrainingSubmissionPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/pet/petting`,
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetPlayerId }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parsePetTrainingSubmissionResponse(response);
  }

  async submitPetTreat(
    token: string,
    villageId: number,
    sequence: number,
    buildingId: number,
    signal?: AbortSignal,
  ): Promise<PetTreatSubmissionPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/pet/treats`,
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sequence, buildingId }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parsePetTreatSubmissionResponse(response);
  }

  async submitTreasurePickup(
    token: string,
    villageId: number,
    sequence: number,
    buildingId: number,
    signal?: AbortSignal,
  ): Promise<TreasurePickupSubmissionPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/treasure-pickups`,
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sequence, buildingId }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseTreasurePickupSubmissionResponse(response);
  }

  async submitSeasonalBadgePickup(
    token: string,
    villageId: number,
    buildingId: number,
    signal?: AbortSignal,
  ): Promise<SeasonalBadgePickupSubmissionPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/seasonal-badge-pickups`,
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ buildingId }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseSeasonalBadgePickupSubmissionResponse(response);
  }

  async updateTabletop(
    token: string,
    villageId: number,
    action: TabletopAction,
    signal: AbortSignal,
  ): Promise<number> {
    return parseGateOfferingRevision(
      await this.request(
        `/api/v2/sessions/current/villages/${String(villageId)}/tabletop-actions`,
        this.authorized(token, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(action),
          signal,
        }),
      ),
    );
  }

  async purchaseBuilding(
    token: string,
    villageId: number,
    sequence: number,
    buildingType: number,
    tileX: number,
    tileZ: number,
    rotation: number,
    gift: BuildingGiftPurchaseDetails | undefined,
    signal?: AbortSignal,
    placementY?: number,
    tabletop?: TabletopPlacement,
    domino?: DominoOptions,
    liveScreenScale?: number,
    placementOffsetX?: number,
    placementOffsetZ?: number,
    placementTiltX?: number,
    placementTiltZ?: number,
  ): Promise<BuildingPurchaseSubmissionPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/buildings`,
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sequence,
          buildingType,
          ...(placementY === undefined ? {} : { placementY }),
          ...(tabletop === undefined ? {} : { tabletop }),
          ...(domino === undefined ? {} : { domino }),
          ...(liveScreenScale === undefined ? {} : { liveScreenScale }),
          ...(placementOffsetX === undefined ? {} : { placementOffsetX }),
          ...(placementOffsetZ === undefined ? {} : { placementOffsetZ }),
          ...(placementTiltX === undefined ? {} : { placementTiltX }),
          ...(placementTiltZ === undefined ? {} : { placementTiltZ }),
          tileX,
          tileZ,
          rotation,
          ...(gift === undefined
            ? {}
            : {
                giftText: gift.text,
                wrappingType: gift.wrappingType ?? 0,
                canOpenAt: gift.canOpenAt ?? -1,
              }),
        }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseBuildingPurchaseSubmissionResponse(response);
  }

  async purchaseNobo(
    token: string,
    villageId: number,
    sequence: number,
    productType: number,
    tileX: number,
    tileZ: number,
    signal?: AbortSignal,
  ): Promise<NoboPurchaseSubmissionPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/nobos`,
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sequence, productType, tileX, tileZ }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseNoboPurchaseSubmissionResponse(response);
  }

  async pickupCustomVehicle(
    token: string,
    villageId: number,
    sequence: number,
    characterId: number,
    signal?: AbortSignal,
  ): Promise<CustomVehiclePickupSubmissionPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/custom-vehicle-pickups`,
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sequence, characterId }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseCustomVehiclePickupSubmissionResponse(response);
  }

  async sellMount(
    token: string,
    villageId: number,
    sequence: number,
    characterId: number,
    signal?: AbortSignal,
  ): Promise<MountSaleSubmissionPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/mount-sales`,
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sequence, characterId }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseMountSaleSubmissionResponse(response);
  }

  async editNobo(
    token: string,
    villageId: number,
    sequence: number,
    operation: NoboEditOperation,
    characterId: number,
    details?: Readonly<{ tileX: number; tileZ: number; sourceVillageId?: number }>,
    signal?: AbortSignal,
  ): Promise<NoboEditSubmissionPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/nobo-edits`,
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sequence, operation, characterId, ...(details ?? {}) }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseNoboEditSubmissionResponse(response);
  }

  async editBuilding(
    token: string,
    villageId: number,
    sequence: number,
    operation: BuildingEditOperation,
    buildingId: number,
    details?:
      | Readonly<{
          tileX: number;
          tileZ: number;
          rotation: number;
          placementY?: number;
          placementOffsetX?: number;
          placementOffsetZ?: number;
          placementTiltX?: number;
          placementTiltZ?: number;
          tabletop?: TabletopPlacement;
          domino?: DominoOptions;
          liveScreenScale?: number;
          sourceVillageId?: number;
        }>
      | Readonly<{ pictureId: number }>
      | Readonly<{ visitorsAllowed: boolean }>
      | Readonly<{ open: boolean }>,
    signal?: AbortSignal,
  ): Promise<BuildingEditSubmissionPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/building-edits`,
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sequence, operation, buildingId, ...(details ?? {}) }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseBuildingEditSubmissionResponse(response);
  }

  async removeCreativeTrees(
    token: string,
    villageId: number,
    sequence: number,
    baseRevision: number,
    treeIds: readonly number[],
    signal: AbortSignal,
  ) {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/creative-tree-removals`,
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sequence, baseRevision, treeIds }),
        signal,
      }),
    );
    if (response === null || typeof response !== 'object' || !('worldUpdate' in response))
      throw new Error('Invalid tree removal response');
    return parseWorldNotificationUpdate(response.worldUpdate);
  }

  async readVillageEditor(
    token: string,
    villageId: number,
    signal?: AbortSignal,
    area?: Readonly<{ chunkX: number; chunkZ: number }>,
  ): Promise<VillageEditorPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/village-editor${area === undefined ? '' : `?chunkX=${String(area.chunkX)}&chunkZ=${String(area.chunkZ)}`}`,
      this.authorized(token, { ...(signal === undefined ? {} : { signal }) }),
    );
    return parseVillageEditorResponse(response);
  }

  async editBuildingBatch(
    token: string,
    villageId: number,
    sequence: number,
    baseRevision: number,
    placements: readonly BuildingBatchPlacementPayload[],
    signal?: AbortSignal,
  ): Promise<BuildingBatchEditSubmissionPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/building-batch-edits`,
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sequence, baseRevision, placements }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseBuildingBatchEditSubmissionResponse(response);
  }

  async pickupBuildingBatch(
    token: string,
    villageId: number,
    sequence: number,
    baseRevision: number,
    buildingIds: readonly number[],
    signal?: AbortSignal,
  ): Promise<BuildingBatchPickupSubmissionPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/building-batch-pickups`,
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sequence, baseRevision, buildingIds }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseBuildingBatchPickupSubmissionResponse(response);
  }

  async gateOfferingResources(token: string, villageId: number, signal: AbortSignal) {
    return parseGateOfferingResources(
      await this.request(
        `/api/v2/sessions/current/villages/${String(villageId)}/gate-offering`,
        this.authorized(token, { signal }),
      ),
    );
  }

  async configureGateOffering(
    token: string,
    villageId: number,
    buildingId: number,
    resources: readonly number[],
    signal: AbortSignal,
  ) {
    return parseGateOfferingRevision(
      await this.request(
        `/api/v2/sessions/current/villages/${String(villageId)}/gate-offering`,
        this.authorized(token, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ buildingId, resources }),
          signal,
        }),
      ),
    );
  }

  async configureBuildingCode(
    token: string,
    villageId: number,
    buildingId: number,
    code: string,
    signal?: AbortSignal,
  ): Promise<BuildingCodeSubmissionPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/building-code`,
      this.authorized(token, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ buildingId, code }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseBuildingCodeSubmissionResponse(response);
  }

  async updateBuildingText(
    token: string,
    villageId: number,
    buildingId: number,
    text: string,
    signal?: AbortSignal,
  ): Promise<BuildingTextSubmissionPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/building-text`,
      this.authorized(token, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ buildingId, text }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseBuildingTextSubmissionResponse(response);
  }

  async readPlayerPictures(
    token: string,
    signal?: AbortSignal,
    playerId?: number,
  ): Promise<PlayerPicturesPayload> {
    const response = await this.request(
      `${playerPicturePath(playerId)}/pictures`,
      this.authorized(token, signal === undefined ? {} : { signal }),
    );
    return parsePlayerPicturesResponse(response);
  }

  async readPlayerProfile(token: string, signal?: AbortSignal): Promise<PlayerProfilePayload> {
    const response = await this.request(
      '/api/v2/sessions/current/profile',
      this.authorized(token, signal === undefined ? {} : { signal }),
    );
    return parsePlayerProfileResponse(response);
  }

  async readRemotePlayerProfile(
    token: string,
    playerId: number,
    signal?: AbortSignal,
  ): Promise<PlayerProfilePayload> {
    const response = await this.request(
      `/api/v2/sessions/current/players/${String(playerId)}/profile`,
      this.authorized(token, signal === undefined ? {} : { signal }),
    );
    return parsePlayerProfileResponse(response);
  }

  async readPlayerVillages(
    token: string,
    playerId: number,
    signal?: AbortSignal,
  ): Promise<PlayerVillagesPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/players/${String(playerId)}/villages`,
      this.authorized(token, signal === undefined ? {} : { signal }),
    );
    return parsePlayerVillagesResponse(response);
  }

  async readPlayerBadges(
    token: string,
    playerId: number,
    signal?: AbortSignal,
  ): Promise<PlayerBadgesPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/players/${String(playerId)}/badges`,
      this.localizedAuthorized(token, signal === undefined ? {} : { signal }),
    );
    return parsePlayerBadgesResponse(response);
  }

  async completeContextorial(
    token: string,
    type: number,
    signal?: AbortSignal,
  ): Promise<ContextorialCompletionPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/contextorial/${String(type)}/complete`,
      this.authorized(token, {
        method: 'POST',
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseContextorialCompletionResponse(response);
  }

  async readBlockedPlayers(token: string, signal?: AbortSignal): Promise<BlockedPlayerListPayload> {
    const response = await this.request(
      '/api/v2/sessions/current/blocked-players',
      this.authorized(token, signal === undefined ? {} : { signal }),
    );
    return parseBlockedPlayerListResponse(response);
  }

  async readOnlinePlayers(token: string, signal?: AbortSignal): Promise<OnlinePlayerListPayload> {
    const response = await this.request(
      '/api/v2/sessions/current/online-players',
      this.localizedAuthorized(token, signal === undefined ? {} : { signal }),
    );
    return parseOnlinePlayerListResponse(response);
  }

  async readHelpDirectory(token: string, signal?: AbortSignal): Promise<HelpDirectoryPayload> {
    const response = await this.request(
      '/api/v2/sessions/current/help-directory',
      this.localizedAuthorized(token, signal === undefined ? {} : { signal }),
    );
    return parseHelpDirectoryResponse(response);
  }

  async readSupportIdentity(token: string, signal?: AbortSignal): Promise<SupportIdentityPayload> {
    const response = await this.request(
      '/api/v2/sessions/current/support',
      this.authorized(token, signal === undefined ? {} : { signal }),
    );
    return parseSupportIdentityResponse(response);
  }

  async submitSupportRequest(
    token: string,
    email: string,
    message: string,
    signal?: AbortSignal,
  ): Promise<void> {
    await this.request(
      '/api/v2/sessions/current/support',
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, message }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
  }

  async readPaymentCatalog(token: string, signal?: AbortSignal): Promise<PaymentCatalogPayload> {
    const response = await this.request(
      '/api/v2/sessions/current/payments/catalog',
      this.authorized(token, signal === undefined ? {} : { signal }),
    );
    return parsePaymentCatalogResponse(response);
  }

  async createPaymentCheckoutSession(
    token: string,
    offerKey: string,
    recipientPlayerId: number,
    signal?: AbortSignal,
  ): Promise<PaymentCheckoutSessionPayload> {
    const response = await this.request(
      '/api/v2/sessions/current/payments/checkout-sessions',
      this.localizedAuthorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ offerKey, recipientPlayerId }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parsePaymentCheckoutSessionResponse(response);
  }

  async createPaymentCustomerPortalSession(
    token: string,
    signal?: AbortSignal,
  ): Promise<PaymentCustomerPortalSessionPayload> {
    const response = await this.request(
      '/api/v2/sessions/current/payments/customer-portal-sessions',
      this.localizedAuthorized(token, {
        method: 'POST',
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parsePaymentCustomerPortalSessionResponse(response);
  }

  async redeemPromoCode(
    token: string,
    code: string,
    signal?: AbortSignal,
  ): Promise<PromoRedemptionPayload> {
    const response = await this.request(
      '/api/v2/sessions/current/promo-redemptions',
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parsePromoRedemptionResponse(response);
  }

  async readWardrobe(token: string, signal?: AbortSignal): Promise<WardrobePayload> {
    const response = await this.request(
      '/api/v2/sessions/current/wardrobe',
      this.localizedAuthorized(token, signal === undefined ? {} : { signal }),
    );
    return parseWardrobeResponse(response);
  }

  async equipWardrobe(
    token: string,
    categoryId: string,
    garmentId: number,
    signal?: AbortSignal,
  ): Promise<WardrobePayload> {
    const response = await this.request(
      '/api/v2/sessions/current/wardrobe/equipped',
      this.localizedAuthorized(token, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ categoryId, garmentId }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseWardrobeResponse(response);
  }

  async purchaseWardrobe(
    token: string,
    categoryId: string,
    garmentId: number,
    selection: WardrobePurchaseSelection,
    signal?: AbortSignal,
  ): Promise<WardrobePayload> {
    const response = await this.request(
      '/api/v2/sessions/current/wardrobe/purchases',
      this.localizedAuthorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ categoryId, garmentId, ...selection }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseWardrobeResponse(response);
  }

  async readVillageVisitors(
    token: string,
    villageId: number,
    signal?: AbortSignal,
  ): Promise<VillageVisitorListPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/visitors`,
      this.authorized(token, signal === undefined ? {} : { signal }),
    );
    return parseVillageVisitorListResponse(response);
  }

  async setRemotePlayerBlocked(
    token: string,
    playerId: number,
    blocked: boolean,
    signal?: AbortSignal,
  ): Promise<PlayerProfilePayload> {
    const response = await this.request(
      `/api/v2/sessions/current/players/${String(playerId)}/block`,
      this.authorized(token, {
        method: blocked ? 'PUT' : 'DELETE',
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parsePlayerProfileResponse(response);
  }

  async readFriendList(
    token: string,
    playerId: number,
    signal?: AbortSignal,
  ): Promise<FriendListPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/players/${String(playerId)}/friends`,
      this.authorized(token, signal === undefined ? {} : { signal }),
    );
    return parseFriendListResponse(response);
  }

  async removeFriend(
    token: string,
    playerId: number,
    signal?: AbortSignal,
  ): Promise<FriendRemovalPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/players/${String(playerId)}/friends`,
      this.authorized(token, {
        method: 'DELETE',
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseFriendRemovalResponse(response);
  }

  async readFriendRelationship(
    token: string,
    playerId: number,
    signal?: AbortSignal,
  ): Promise<FriendRelationshipPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/players/${String(playerId)}/friend-request`,
      this.authorized(token, signal === undefined ? {} : { signal }),
    );
    return parseFriendRelationshipResponse(response);
  }

  async updateFriendRequest(
    token: string,
    playerId: number,
    action: 'request' | 'accept' | 'decline',
    signal?: AbortSignal,
  ): Promise<FriendRequestMutationPayload> {
    const method = action === 'request' ? 'POST' : action === 'accept' ? 'PUT' : 'DELETE';
    const response = await this.request(
      `/api/v2/sessions/current/players/${String(playerId)}/friend-request`,
      this.authorized(token, {
        method,
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseFriendRequestMutationResponse(response);
  }

  async travelToPlayer(
    token: string,
    playerId: number,
    sequence: number,
    signal?: AbortSignal,
  ): Promise<VillageTravelPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/players/${String(playerId)}/travel`,
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sequence }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseVillageTravelResponse(response);
  }

  async travelToPlayerVillage(
    token: string,
    playerId: number,
    villageId: number,
    sequence: number,
    signal?: AbortSignal,
  ): Promise<VillageTravelPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/players/${String(playerId)}/villages/${String(villageId)}/travel`,
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sequence }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseVillageTravelResponse(response);
  }

  async readOwnedVillages(
    token: string,
    signal?: AbortSignal,
  ): Promise<OwnedVillagesResponsePayload> {
    const response = await this.request(
      '/api/v2/sessions/current/owned-villages',
      this.authorized(token, signal === undefined ? {} : { signal }),
    );
    return parseOwnedVillagesResponse(response, token);
  }

  async unlockOwnedVillage(
    token: string,
    slot: number,
    signal?: AbortSignal,
  ): Promise<OwnedVillagesResponsePayload> {
    const response = await this.request(
      '/api/v2/sessions/current/owned-villages',
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slot }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseOwnedVillagesResponse(response, token);
  }

  async travelToOwnedVillage(
    token: string,
    villageId: number,
    sequence: number,
    signal?: AbortSignal,
  ): Promise<VillageTravelPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/owned-villages/${String(villageId)}/travel`,
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sequence }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseVillageTravelResponse(response);
  }

  async travelToSpecialVillage(
    token: string,
    villageId: number,
    sequence: number,
    signal?: AbortSignal,
  ): Promise<VillageTravelPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/special-villages/${String(villageId)}/travel`,
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sequence }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseVillageTravelResponse(response);
  }

  async travelThroughBuilding(
    token: string,
    villageId: number,
    travelSequence: number,
    sequence: number,
    sourceBuildingId: number,
    signal?: AbortSignal,
  ): Promise<VillageTravelPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/building-travel`,
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ travelSequence, sequence, sourceBuildingId }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseVillageTravelResponse(response);
  }

  async readGuestbook(
    token: string,
    playerId: number,
    offset = 0,
    signal?: AbortSignal,
  ): Promise<GuestbookPayload> {
    const query = offset === 0 ? '' : `?offset=${String(offset)}`;
    const response = await this.request(
      `/api/v2/sessions/current/players/${String(playerId)}/guestbook${query}`,
      this.authorized(token, signal === undefined ? undefined : { signal }),
    );
    return parseGuestbookResponse(response);
  }

  async readGuestbookAvailability(
    token: string,
    signal?: AbortSignal,
  ): Promise<GuestbookAvailabilityPayload> {
    const response = await this.request(
      '/api/v2/sessions/current/guestbook/availability',
      this.authorized(token, signal === undefined ? undefined : { signal }),
    );
    return parseGuestbookAvailabilityResponse(response);
  }

  async clearGuestbookAvailability(
    token: string,
    signal?: AbortSignal,
  ): Promise<GuestbookAvailabilityPayload> {
    const response = await this.request(
      '/api/v2/sessions/current/guestbook/availability',
      this.authorized(token, {
        method: 'DELETE',
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseGuestbookAvailabilityResponse(response);
  }

  async addGuestbookEntry(
    token: string,
    playerId: number,
    text: string,
    privatePost: boolean,
    signal?: AbortSignal,
  ): Promise<GuestbookSubmissionPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/players/${String(playerId)}/guestbook`,
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, private: privatePost }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseGuestbookSubmissionResponse(response);
  }

  async readGuestbookHistory(
    token: string,
    playerId: number,
    postId: number,
    entryOffset: number,
    historyOffset = 0,
    signal?: AbortSignal,
  ): Promise<GuestbookHistoryPayload> {
    const parameters: string[] = [];
    if (historyOffset !== 0) parameters.push(`offset=${String(historyOffset)}`);
    if (entryOffset !== 0) parameters.push(`entryOffset=${String(entryOffset)}`);
    const query = parameters.length === 0 ? '' : `?${parameters.join('&')}`;
    const response = await this.request(
      `/api/v2/sessions/current/players/${String(playerId)}/guestbook/${String(postId)}/history${query}`,
      this.authorized(token, signal === undefined ? undefined : { signal }),
    );
    return parseGuestbookHistoryResponse(response);
  }

  async removeGuestbookEntry(
    token: string,
    playerId: number,
    postId: number,
    offset: number,
    signal?: AbortSignal,
  ): Promise<GuestbookMutationPayload> {
    const query = offset === 0 ? '' : `?offset=${String(offset)}`;
    const response = await this.request(
      `/api/v2/sessions/current/players/${String(playerId)}/guestbook/${String(postId)}${query}`,
      this.authorized(token, {
        method: 'DELETE',
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseGuestbookMutationResponse(response);
  }

  async setGuestbookEntryPrivate(
    token: string,
    playerId: number,
    postId: number,
    privatePost: boolean,
    offset: number,
    signal?: AbortSignal,
  ): Promise<GuestbookMutationPayload> {
    const query = offset === 0 ? '' : `?offset=${String(offset)}`;
    const response = await this.request(
      `/api/v2/sessions/current/players/${String(playerId)}/guestbook/${String(postId)}/privacy${query}`,
      this.authorized(token, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ private: privatePost }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseGuestbookMutationResponse(response);
  }

  async updatePresentationPicture(
    token: string,
    pictureId: number | null,
    signal?: AbortSignal,
    playerId?: number,
  ): Promise<PlayerProfilePayload> {
    const response = await this.request(
      `${playerPicturePath(playerId)}/profile/picture`,
      this.authorized(token, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pictureId }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parsePlayerProfileResponse(response);
  }

  async updatePlayerStatus(
    token: string,
    status: string,
    signal?: AbortSignal,
  ): Promise<PlayerProfilePayload> {
    const response = await this.request(
      '/api/v2/sessions/current/profile/status',
      this.authorized(token, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parsePlayerProfileResponse(response);
  }

  async updatePreferredLanguages(
    token: string,
    languages: readonly string[],
    signal?: AbortSignal,
  ): Promise<PlayerProfilePayload> {
    const response = await this.request(
      '/api/v2/sessions/current/profile/languages',
      this.authorized(token, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ languages }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parsePlayerProfileResponse(response);
  }

  async updatePlayerPersonalInformation(
    token: string,
    update: PlayerPersonalInformationUpdate,
    signal?: AbortSignal,
  ): Promise<PlayerProfilePayload> {
    const response = await this.request(
      '/api/v2/sessions/current/profile/personal-information',
      this.authorized(token, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(update),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parsePlayerProfileResponse(response);
  }

  async readPlayerPicture(
    token: string,
    pictureId: number,
    thumbnail: boolean,
    signal?: AbortSignal,
  ): Promise<Blob> {
    let response: Response;
    try {
      response = await fetch(
        `/api/v2/sessions/current/pictures/${String(pictureId)}?thumbnail=${String(thumbnail)}`,
        this.authorized(token, signal === undefined ? {} : { signal }),
      );
    } catch (cause) {
      throw new GatewayError('gateway_unreachable', 'The Nord server is unavailable.', 0, cause);
    }
    if (!response.ok) {
      let payload: unknown;
      try {
        payload = await response.json();
        const error = parseErrorResponse(payload);
        throw new GatewayError(error.code, error.message, response.status);
      } catch (cause) {
        if (cause instanceof GatewayError) throw cause;
        throw new GatewayError(
          'invalid_gateway_response',
          'The Nord server returned an unreadable response.',
          response.status,
          cause,
        );
      }
    }
    return response.blob();
  }

  async readRemotePlayerProfilePicture(
    token: string,
    playerId: number,
    pictureId: number,
    thumbnail: boolean,
    signal?: AbortSignal,
  ): Promise<Blob> {
    let response: Response;
    try {
      response = await fetch(
        `/api/v2/sessions/current/players/${String(playerId)}/profile/picture/${String(pictureId)}?thumbnail=${String(thumbnail)}`,
        this.authorized(token, signal === undefined ? {} : { signal }),
      );
    } catch (cause) {
      throw new GatewayError('gateway_unreachable', 'The Nord server is unavailable.', 0, cause);
    }
    if (!response.ok) {
      let payload: unknown;
      try {
        payload = await response.json();
        const error = parseErrorResponse(payload);
        throw new GatewayError(error.code, error.message, response.status);
      } catch (cause) {
        if (cause instanceof GatewayError) throw cause;
        throw new GatewayError(
          'invalid_gateway_response',
          'The Nord server returned an unreadable response.',
          response.status,
          cause,
        );
      }
    }
    return response.blob();
  }

  async deletePlayerPicture(
    token: string,
    pictureId: number,
    signal?: AbortSignal,
    playerId?: number,
  ): Promise<PictureDeletionPayload> {
    const response = await this.request(
      `${playerPicturePath(playerId)}/pictures/${String(pictureId)}`,
      this.authorized(token, {
        method: 'DELETE',
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parsePictureDeletionResponse(response);
  }

  async uploadPlayerPicture(
    token: string,
    imageBase64: string,
    thumbnailBase64: string,
    signal?: AbortSignal,
    playerId?: number,
  ): Promise<PictureUploadPayload> {
    const response = await this.request(
      `${playerPicturePath(playerId)}/pictures`,
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imageBase64, thumbnailBase64 }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parsePictureUploadResponse(response);
  }

  async uploadScreenshot(
    token: string,
    imageBase64: string,
    signal?: AbortSignal,
  ): Promise<ScreenshotUploadPayload> {
    const response = await this.request(
      '/api/v2/sessions/current/screenshots',
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imageBase64 }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseScreenshotUploadResponse(response);
  }

  async submitBuildingProduction(
    token: string,
    villageId: number,
    sequence: number,
    action: BuildingProductionAction,
    buildingId: number,
    resourceId: number,
    signal?: AbortSignal,
  ): Promise<BuildingProductionSubmissionPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/building-production`,
      this.localizedAuthorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sequence, action, buildingId, resourceId }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseBuildingProductionSubmissionResponse(response);
  }

  async readMarketplace(token: string, signal?: AbortSignal): Promise<MarketplacePayload> {
    const response = await this.request(
      '/api/v2/sessions/current/marketplace',
      this.localizedAuthorized(token, {
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseMarketplaceResponse(response);
  }

  async createMarketplaceOrder(
    token: string,
    side: 'buy' | 'sell',
    resourceId: number,
    quantity: number,
    unitPrice: number,
    signal?: AbortSignal,
    auction?: MarketplaceAuctionOptions,
  ): Promise<MarketplacePayload> {
    const response = await this.request(
      '/api/v2/sessions/current/marketplace/orders',
      this.localizedAuthorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          side,
          resourceId,
          quantity,
          unitPrice,
          ...(auction ?? {}),
        }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseMarketplaceResponse(response);
  }

  async transferMarketplaceInventory(
    token: string,
    direction: 'deposit' | 'withdraw',
    resourceId: number,
    quantity: number,
    signal?: AbortSignal,
  ): Promise<MarketplacePayload> {
    const response = await this.request(
      '/api/v2/sessions/current/marketplace/inventory-transfers',
      this.localizedAuthorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ direction, resourceId, quantity }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseMarketplaceResponse(response);
  }

  async fulfillMarketplaceOrder(
    token: string,
    orderId: number,
    quantity: number,
    signal?: AbortSignal,
  ): Promise<MarketplacePayload> {
    const response = await this.request(
      `/api/v2/sessions/current/marketplace/orders/${String(orderId)}/fulfillments`,
      this.localizedAuthorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quantity }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseMarketplaceResponse(response);
  }

  async bidMarketplaceOrder(
    token: string,
    orderId: number,
    unitPrice: number,
    signal?: AbortSignal,
  ): Promise<MarketplacePayload> {
    const response = await this.request(
      `/api/v2/sessions/current/marketplace/orders/${String(orderId)}/bids`,
      this.localizedAuthorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ unitPrice }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseMarketplaceResponse(response);
  }

  async cancelMarketplaceOrder(
    token: string,
    orderId: number,
    signal?: AbortSignal,
  ): Promise<MarketplacePayload> {
    const response = await this.request(
      `/api/v2/sessions/current/marketplace/orders/${String(orderId)}`,
      this.localizedAuthorized(token, {
        method: 'DELETE',
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseMarketplaceResponse(response);
  }

  async submitInventoryMove(
    token: string,
    villageId: number,
    sequence: number,
    sourceIndex: number,
    destinationIndex: number,
    signal?: AbortSignal,
  ): Promise<InventoryMoveSubmissionPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/inventory-moves`,
      this.localizedAuthorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sequence, sourceIndex, destinationIndex }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseInventoryMoveSubmissionResponse(response);
  }

  async submitMountTransition(
    token: string,
    villageId: number,
    sequence: number,
    action: MountTransitionAction,
    characterId: number,
    signal?: AbortSignal,
  ): Promise<MountTransitionSubmissionPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/mount-transitions`,
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sequence, action, characterId }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseMountTransitionSubmissionResponse(response);
  }

  async updateMountName(
    token: string,
    villageId: number,
    characterId: number,
    mountName: string,
    signal?: AbortSignal,
  ): Promise<MountNameSubmissionPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/mount-name`,
      this.authorized(token, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ characterId, mountName }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseMountNameSubmissionResponse(response);
  }

  async submitBuildingTeleport(
    token: string,
    villageId: number,
    sequence: number,
    sourceBuildingId: number,
    trigger: 'click' | 'collision',
    signal?: AbortSignal,
  ): Promise<BuildingTeleportSubmissionPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/building-teleports`,
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sequence, sourceBuildingId, trigger }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseBuildingTeleportSubmissionResponse(response);
  }

  async readMountShop(
    token: string,
    villageId: number,
    shop: MountShopId,
    signal?: AbortSignal,
  ): Promise<MountShopPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/mount-shops/${shop}`,
      this.authorized(token, {
        method: 'GET',
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseMountShopResponse(response);
  }

  async purchaseMount(
    token: string,
    villageId: number,
    sequence: number,
    shop: MountShopId,
    mountType: number,
    mountName: string,
    signal?: AbortSignal,
  ): Promise<MountPurchaseSubmissionPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/mount-purchases`,
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sequence, shop, mountType, mountName }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseMountPurchaseSubmissionResponse(response);
  }

  async readPetShop(
    token: string,
    villageId: number,
    buildingId: number,
    signal?: AbortSignal,
  ): Promise<PetShopPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/pet-shops/${String(buildingId)}`,
      this.authorized(token, {
        method: 'GET',
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parsePetShopResponse(response);
  }

  async purchasePet(
    token: string,
    villageId: number,
    sequence: number,
    buildingId: number,
    petType: number,
    petName: string,
    signal?: AbortSignal,
  ): Promise<PetPurchaseSubmissionPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/pet-purchases`,
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sequence, buildingId, petType, petName }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parsePetPurchaseSubmissionResponse(response);
  }

  async readThemeShop(
    token: string,
    villageId: number,
    buildingId: number,
    signal?: AbortSignal,
  ): Promise<ThemeShopPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/theme-shops/${String(buildingId)}`,
      this.authorized(token, {
        method: 'GET',
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseThemeShopResponse(response);
  }

  async purchaseTheme(
    token: string,
    sourceVillageId: number,
    sequence: number,
    buildingId: number,
    themeId: number,
    targetVillageId: number,
    signal?: AbortSignal,
  ): Promise<ThemePurchaseSubmissionPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(sourceVillageId)}/theme-purchases`,
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sequence, buildingId, themeId, targetVillageId }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseThemePurchaseSubmissionResponse(response);
  }

  async submitInventoryActivation(
    token: string,
    villageId: number,
    sequence: number,
    sourceIndex: number,
    signal?: AbortSignal,
  ): Promise<InventoryActivationSubmissionPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/inventory-activations`,
      this.localizedAuthorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sequence, sourceIndex }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseInventoryActivationSubmissionResponse(response);
  }

  async loadWeatherInventory(
    token: string,
    villageId: number,
    signal?: AbortSignal,
  ): Promise<WeatherControlsPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/weather`,
      this.authorized(token, {
        method: 'GET',
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseWeatherInventoryResponse(response);
  }

  async submitWeatherActivation(
    token: string,
    villageId: number,
    sequence: number,
    kind: WeatherKind,
    signal?: AbortSignal,
  ): Promise<WeatherActivationSubmissionPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/weather-activations`,
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sequence, kind }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseWeatherActivationSubmissionResponse(response);
  }

  async submitResourceSlotMove(
    token: string,
    villageId: number,
    sequence: number,
    sourceArea: ResourceSlotArea,
    sourceIndex: number,
    destinationArea: ResourceSlotArea,
    destinationIndex: number,
    signal?: AbortSignal,
  ): Promise<ResourceSlotMoveSubmissionPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/resource-slot-moves`,
      this.localizedAuthorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sequence,
          sourceArea,
          sourceIndex,
          destinationArea,
          destinationIndex,
        }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseResourceSlotMoveSubmissionResponse(response);
  }

  async readTombola(
    token: string,
    villageId: number,
    signal?: AbortSignal,
  ): Promise<TombolaPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/tombola`,
      this.localizedAuthorized(token, signal === undefined ? {} : { signal }),
    );
    return parseTombolaResponse(response);
  }

  async submitTombola(
    token: string,
    villageId: number,
    sequence: number,
    action: 'start' | 'stop',
    signal?: AbortSignal,
  ): Promise<TombolaPayload>;
  async submitTombola(
    token: string,
    villageId: number,
    sequence: number,
    action: 'finish',
    signal?: AbortSignal,
  ): Promise<void>;
  async submitTombola(
    token: string,
    villageId: number,
    sequence: number,
    action: Exclude<TombolaAction, 'inspect'>,
    signal?: AbortSignal,
  ): Promise<TombolaPayload | void> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/tombola`,
      this.localizedAuthorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sequence, action }),
        ...(signal === undefined ? {} : { signal }),
      }),
      action === 'finish',
    );
    if (action === 'finish') return;
    return parseTombolaResponse(response);
  }

  async submitPetFollowing(
    token: string,
    villageId: number,
    sequence: number,
    following: boolean,
    characterId: number,
    position: Readonly<{ posX: number; posZ: number }> | undefined,
    signal?: AbortSignal,
  ): Promise<PetFollowingSubmissionPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/pet/following`,
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sequence,
          following,
          characterId,
          ...(position ?? {}),
        }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parsePetFollowingSubmissionResponse(response);
  }

  async submitPetHouse(
    token: string,
    villageId: number,
    sequence: number,
    buildingId: number,
    signal?: AbortSignal,
  ): Promise<PetHouseSubmissionPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/villages/${String(villageId)}/pet/houses`,
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sequence, buildingId }),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parsePetHouseSubmissionResponse(response);
  }

  async inspectWorldHistory(
    token: string,
    villageId: number,
    body: WorldHistoryRequest,
    signal?: AbortSignal,
  ): Promise<WorldHistoryInspection> {
    return parseWorldHistory(
      await this.request(
        `/api/v2/sessions/current/villages/${String(villageId)}/world-history/inspect`,
        this.authorized(token, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
          ...(signal === undefined ? {} : { signal }),
        }),
      ),
    );
  }

  async rollbackWorldHistory(
    token: string,
    villageId: number,
    body: WorldHistoryRequest,
    signal?: AbortSignal,
  ): Promise<WorldHistorySubmission> {
    return parseWorldHistorySubmission(
      await this.request(
        `/api/v2/sessions/current/villages/${String(villageId)}/world-history/rollback`,
        this.authorized(token, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
          ...(signal === undefined ? {} : { signal }),
        }),
      ),
    );
  }

  async readModerationDashboard(
    token: string,
    signal?: AbortSignal,
  ): Promise<ModerationDashboardPayload> {
    const response = await this.request(
      '/api/v2/sessions/current/moderation/dashboard',
      this.authorized(token, signal === undefined ? {} : { signal }),
    );
    return parseModerationDashboardResponse(response);
  }

  async readModerationServerStatus(
    token: string,
    signal?: AbortSignal,
  ): Promise<ModerationServerStatusPayload> {
    const response = await this.request(
      '/api/v2/sessions/current/moderation/server-status',
      this.authorized(token, signal === undefined ? {} : { signal }),
    );
    return parseModerationServerStatusResponse(response);
  }

  async readModerationOnlinePlayers(
    token: string,
    signal?: AbortSignal,
  ): Promise<ModerationOnlinePlayersPayload> {
    const response = await this.request(
      '/api/v2/sessions/current/moderation/online-players?limit=500',
      this.authorized(token, signal === undefined ? {} : { signal }),
    );
    return parseModerationOnlinePlayersResponse(response);
  }

  async searchModerationPlayers(
    token: string,
    query: string,
    page: number,
    sort: ModerationPlayerSort,
    direction: ModerationSortDirection,
    signal?: AbortSignal,
  ): Promise<ModerationPlayerListPayload> {
    const parameters = new URLSearchParams({
      query,
      page: String(page),
      limit: '50',
      sort,
      direction,
    });
    const response = await this.request(
      `/api/v2/sessions/current/moderation/players?${parameters.toString()}`,
      this.authorized(token, signal === undefined ? {} : { signal }),
    );
    return parseModerationPlayerListResponse(response);
  }

  async readModerationPlayer(
    token: string,
    playerId: number,
    signal?: AbortSignal,
  ): Promise<ModerationPlayerDetailPayload> {
    const response = await this.request(
      `/api/v2/sessions/current/moderation/players/${String(playerId)}`,
      this.authorized(token, signal === undefined ? {} : { signal }),
    );
    const player = parseModerationPlayerResponse(response);
    if (moderationPlayerResponseHasStatusMessage(response)) return player;
    const profile = await this.readRemotePlayerProfile(token, playerId, signal);
    return { ...player, statusMessage: profile.presentation };
  }

  async readModerationCommunications(
    token: string,
    signal?: AbortSignal,
  ): Promise<readonly ModerationCommunicationPayload[]> {
    const response = await this.request(
      '/api/v2/sessions/current/moderation/communications?limit=30',
      this.authorized(token, signal === undefined ? {} : { signal }),
    );
    return parseModerationCommunicationsResponse(response);
  }

  async downloadModerationCommunications(token: string, signal?: AbortSignal): Promise<Blob> {
    return this.requestBlob(
      '/api/v2/sessions/current/moderation/communications/download',
      this.authorized(token, signal === undefined ? {} : { signal }),
    );
  }

  async readModerationBans(
    token: string,
    activeOnly: boolean,
    signal?: AbortSignal,
  ): Promise<readonly ModerationBanPayload[]> {
    const response = await this.request(
      `/api/v2/sessions/current/moderation/bans?activeOnly=${activeOnly ? '1' : '0'}`,
      this.authorized(token, signal === undefined ? {} : { signal }),
    );
    return parseModerationBansResponse(response);
  }

  async sendModerationPopup(
    token: string,
    playerId: number,
    message: string,
    signal?: AbortSignal,
  ): Promise<ModerationActionPayload> {
    return this.moderationMutation(
      token,
      `/api/v2/sessions/current/moderation/players/${String(playerId)}/popup`,
      { message },
      signal,
    );
  }

  async saveModerationPlayerNote(
    token: string,
    playerId: number,
    note: string,
    signal?: AbortSignal,
  ): Promise<ModerationActionPayload> {
    return this.moderationMutation(
      token,
      `/api/v2/sessions/current/moderation/players/${String(playerId)}/note`,
      { note },
      signal,
    );
  }

  async saveModerationPlayerStatus(
    token: string,
    playerId: number,
    statusMessage: string,
    signal?: AbortSignal,
  ): Promise<ModerationActionPayload> {
    try {
      return await this.moderationMutation(
        token,
        `/api/v2/sessions/current/moderation/players/${String(playerId)}/status-message`,
        { statusMessage },
        signal,
      );
    } catch (error) {
      if (error instanceof GatewayError && error.status === 404) {
        throw new GatewayError(
          'moderation_status_message_unavailable',
          'Status-message editing requires the updated Go server. Rebuild and restart it, then try again.',
          error.status,
          error,
        );
      }
      throw error;
    }
  }

  async sendGlobalModerationPopup(
    token: string,
    message: string,
    signal?: AbortSignal,
  ): Promise<ModerationActionPayload> {
    return this.moderationMutation(
      token,
      '/api/v2/sessions/current/moderation/popups/global',
      { message },
      signal,
    );
  }

  async setModerationSpeedBoost(
    token: string,
    enabled: boolean,
    signal?: AbortSignal,
  ): Promise<ModerationActionPayload> {
    return this.moderationMutation(
      token,
      '/api/v2/sessions/current/moderation/speed-boost',
      { enabled },
      signal,
    );
  }

  async setModerationGhostMode(
    token: string,
    enabled: boolean,
    signal?: AbortSignal,
  ): Promise<ModerationActionPayload> {
    return this.moderationMutation(
      token,
      '/api/v2/sessions/current/moderation/ghost-mode',
      { enabled },
      signal,
    );
  }

  async setModerationSillyGoose(
    token: string,
    playerId: number,
    enabled: boolean,
    durationMinutes: number,
    signal?: AbortSignal,
  ): Promise<ModerationActionPayload> {
    return this.moderationMutation(
      token,
      `/api/v2/sessions/current/moderation/players/${String(playerId)}/silly-goose`,
      { enabled, durationMinutes },
      signal,
    );
  }

  async kickModerationPlayer(
    token: string,
    playerId: number,
    signal?: AbortSignal,
  ): Promise<ModerationActionPayload> {
    return this.moderationMutation(
      token,
      `/api/v2/sessions/current/moderation/players/${String(playerId)}/kick`,
      {},
      signal,
    );
  }

  async banModerationPlayer(
    token: string,
    playerId: number,
    reason: string,
    durationMinutes: number,
    signal?: AbortSignal,
  ): Promise<ModerationActionPayload> {
    return this.moderationMutation(
      token,
      `/api/v2/sessions/current/moderation/players/${String(playerId)}/ban`,
      { reason, durationMinutes },
      signal,
    );
  }

  async banModerationIP(
    token: string,
    ip: string,
    reason: string,
    durationMinutes: number,
    signal?: AbortSignal,
  ): Promise<ModerationActionPayload> {
    return this.moderationMutation(
      token,
      '/api/v2/sessions/current/moderation/bans/ip',
      { ip, reason, durationMinutes },
      signal,
    );
  }

  async revokeModerationBan(
    token: string,
    banId: number,
    signal?: AbortSignal,
  ): Promise<ModerationActionPayload> {
    return this.moderationMutation(
      token,
      `/api/v2/sessions/current/moderation/bans/${String(banId)}/revoke`,
      {},
      signal,
    );
  }

  private async moderationMutation(
    token: string,
    path: string,
    body: Readonly<Record<string, unknown>>,
    signal?: AbortSignal,
  ): Promise<ModerationActionPayload> {
    const response = await this.request(
      path,
      this.authorized(token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        ...(signal === undefined ? {} : { signal }),
      }),
    );
    return parseModerationActionResponse(response);
  }

  private authorized(token: string, init: RequestInit = {}): RequestInit {
    const headers = new Headers(init.headers);
    headers.set('Authorization', `Bearer ${token}`);
    const method = (init.method ?? 'GET').toUpperCase();
    if (
      method !== 'GET' &&
      method !== 'HEAD' &&
      method !== 'OPTIONS' &&
      !headers.has('Idempotency-Key')
    ) {
      headers.set('Idempotency-Key', createIdempotencyKey());
    }
    return { ...init, headers };
  }

  private localizedAuthorized(token: string, init: RequestInit = {}): RequestInit {
    const request = this.authorized(token, init);
    const headers = new Headers(request.headers);
    headers.set('X-Nord-Language', this.language);
    return { ...request, headers };
  }

  private async request(path: string, init: RequestInit, allowEmpty = false): Promise<unknown> {
    let response: Response;
    try {
      response = await fetch(path, init);
    } catch (cause) {
      throw new GatewayError('gateway_unreachable', 'The Nord server is unavailable.', 0, cause);
    }
    if (allowEmpty && response.status === 204) {
      return undefined;
    }
    let payload: unknown;
    try {
      payload = await response.json();
    } catch (cause) {
      throw new GatewayError(
        'invalid_gateway_response',
        'The Nord server returned an unreadable response.',
        response.status,
        cause,
      );
    }
    if (!response.ok) {
      try {
        const error = parseErrorResponse(payload);
        throw new GatewayError(
          error.code,
          error.message,
          response.status,
          undefined,
          error.creditsAfter,
          error.blockedUntil,
          error.blockReason,
          error.requiredVersion,
          error.serverMessage,
          error.mountTransitionReconciliation,
          error.marketplaceTradeLimit,
          error.movementRecovery,
          error.buildingBatch,
        );
      } catch (cause) {
        if (cause instanceof GatewayError) throw cause;
        throw new GatewayError(
          'gateway_error',
          'The Nord server rejected the request.',
          response.status,
          cause,
        );
      }
    }
    return payload;
  }

  private async requestBlob(path: string, init: RequestInit): Promise<Blob> {
    let response: Response;
    try {
      response = await fetch(path, init);
    } catch (cause) {
      throw new GatewayError('gateway_unreachable', 'The Nord server is unavailable.', 0, cause);
    }
    if (!response.ok) {
      try {
        const error = parseErrorResponse(await response.json());
        throw new GatewayError(error.code, error.message, response.status);
      } catch (cause) {
        if (cause instanceof GatewayError) throw cause;
        throw new GatewayError(
          'invalid_gateway_response',
          'The Nord server returned an unreadable response.',
          response.status,
          cause,
        );
      }
    }
    return response.blob();
  }
}

function createIdempotencyKey(): string {
  return generateBrowserUuid();
}

function moderationPlayerResponseHasStatusMessage(response: unknown): boolean {
  if (typeof response !== 'object' || response === null || Array.isArray(response)) return false;
  const player = (response as Record<string, unknown>).player;
  return (
    typeof player === 'object' &&
    player !== null &&
    !Array.isArray(player) &&
    Object.hasOwn(player, 'statusMessage')
  );
}

function isCanonicalSessionToken(token: string): boolean {
  return /^[A-Za-z0-9_-]{43}$/.test(token);
}

export class GatewayError extends Error {
  override readonly name = 'GatewayError';

  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    options?: unknown,
    readonly creditsAfter?: number,
    readonly blockedUntil?: number,
    readonly blockReason?: string,
    readonly requiredVersion?: string,
    readonly serverMessage?: string,
    readonly mountTransitionReconciliation?: MountTransitionReconciliationPayload,
    readonly marketplaceTradeLimit?: import('./contracts').MarketplaceTradeLimitPayload,
    readonly movementRecovery?: import('./contracts').MovementRecoveryPayload,
    readonly buildingBatch?: import('./contracts').BuildingBatchErrorDetails,
  ) {
    super(message, options === undefined ? undefined : { cause: options });
  }
}

function parseWorldRealtimeTicket(value: unknown): WorldRealtimeTicket {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new ContractError('realtime ticket response is invalid');
  }
  const entry = (value as Record<string, unknown>).realtimeTicket;
  if (typeof entry !== 'object' || entry === null || Array.isArray(entry)) {
    throw new ContractError('realtime ticket is missing');
  }
  const ticket = entry as Record<string, unknown>;
  if (
    typeof ticket.ticket !== 'string' ||
    ticket.ticket.length < 32 ||
    typeof ticket.realtimePath !== 'string' ||
    ticket.realtimePath !== '/api/v2/world-realtime' ||
    ticket.subprotocol !== 'nord-world-v2' ||
    typeof ticket.sessionGeneration !== 'number' ||
    !Number.isSafeInteger(ticket.sessionGeneration) ||
    ticket.sessionGeneration < 1
  ) {
    throw new ContractError('realtime ticket fields are invalid');
  }
  return {
    ticket: ticket.ticket,
    realtimePath: ticket.realtimePath,
    subprotocol: ticket.subprotocol,
    sessionGeneration: ticket.sessionGeneration,
  };
}

function playerPicturePath(playerId?: number): string {
  return playerId === undefined
    ? '/api/v2/sessions/current'
    : `/api/v2/sessions/current/players/${String(playerId)}`;
}
